import styled from 'styled-components';
import { media } from './StyledCalendarBase';

// Estilos específicos para la vista Week
export const WeekHeaderRow = styled.div`
    display: grid;
    grid-template-columns: 120px 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    border-bottom: 1px solid hsl(220, 13%, 91%);
    width: 100%;
    position: sticky;
    top: 0;
    background: white;
    z-index: 5;
    
    ${media.tablet} {
        grid-template-columns: 80px 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }
    
    ${media.mobile} {
        display: none;
    }
`;

// Celda del header semanal.
// Antes mostraba el nombre del día arriba y el número abajo.
// Ahora lo mostramos más compacto en una sola línea para ganar altura.
export const WeekHeaderCell = styled.div`
    padding: 7px 6px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.active ? 'hsl(24, 85%, 40%)' : 'hsl(0, 0%, 30%)'};
    border-left: ${props => props.isFirst ? 'none' : '1px solid hsl(220, 13%, 91%)'};
    border-right: ${props => props.isFirst ? '1px solid hsl(220, 13%, 91%)' : 'none'};
    min-height: 42px;
    box-sizing: border-box;
    
    ${media.tablet} {
        padding: 6px 4px;
        font-size: 11px;
        gap: 6px;
        min-height: 38px;
    }
`;

// Círculo con el número del día.
// Lo achicamos para que el header semanal no robe tanto espacio.
export const WeekHeaderBadge = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${props => props.active ? 'hsl(24, 85%, 60%)' : 'transparent'};
    color: ${props => props.active ? '#FFFFFF' : 'hsl(0, 0%, 30%)'};
    font-size: 12px;
    font-weight: 600;
    border: ${props => props.active ? 'none' : '1px solid hsl(220, 13%, 91%)'};
    flex-shrink: 0;
    
    ${media.tablet} {
        width: 24px;
        height: 24px;
        font-size: 11px;
    }
`;

export const WeekGrid = styled.div`
    display: grid;
    grid-template-columns: 120px 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    width: 100%;
    
    ${media.tablet} {
        grid-template-columns: 80px 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }
    
    ${media.mobile} {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
`;

export const WeekContentContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    height: 100%;
    min-height: 0;
    
    ${media.mobile} {
        overflow-x: visible;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
    }
`;

export const TimeColumn = styled.div`
    background: hsl(210, 40%, 98%);
    height: ${props => props.gridHeight || 1200}px;
    border-right: 1px solid hsl(220, 13%, 91%);
    
    ${media.tablet} {
        height: ${props => props.gridHeight || 1000}px;
    }
    
    ${media.mobile} {
        display: none;
    }
`;

// Celda de la columna de horarios.
// Usa slotHeight para que cada hora pueda estirarse en pantallas grandes
// y no quede espacio blanco debajo de la última hora visible.
export const TimeCell = styled.div`
    height: ${props => props.slotHeight || 66.67}px;
    padding: 14px 18px;
    font-size: 14px;
    font-weight: 600;
    color: hsl(0, 0%, 35%);
    border-bottom: 1px solid hsl(220, 13%, 91%);
    display: flex;
    align-items: center;
    box-sizing: border-box;
    
    ${media.tablet} {
        height: ${props => props.slotHeight || 55}px;
        padding: 10px 14px;
        font-size: 13px;
    }
`;

export const DayColumn = styled.div`
    position: relative;
    border-left: ${props => props.isFirst ? 'none' : '1px solid hsl(220, 13%, 91%)'};
    border-right: ${props => props.isFirst ? '1px solid hsl(220, 13%, 91%)' : 'none'};
    height: ${props => props.gridHeight || 1200}px;
    
    ${media.tablet} {
        height: ${props => props.gridHeight || 1000}px;
    }
    
    ${media.mobile} {
        height: auto;
        border: 1px solid hsl(220, 13%, 91%);
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 8px;
    }
`;

// Celda horaria de cada día.
// Usa slotHeight para coincidir con la columna de horarios.
// Esto permite que la grilla se estire cuando hay pantalla grande.
export const DayCell = styled.div`
    height: ${props => props.slotHeight || 66.67}px;
    border-bottom: 1px solid hsl(220, 13%, 91%);
    transition: background 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    background: ${props => props.isPast ? 'hsl(220, 13%, 98%)' : '#FFFFFF'};
    opacity: ${props => props.isPast ? 0.72 : 1};

    &:hover {
        background: hsl(210, 40%, 96%);
        box-shadow: inset 0 0 0 2px hsl(24, 85%, 60%);
    }

    &:active {
        background: hsl(210, 40%, 92%);
        box-shadow: inset 0 0 0 2px hsl(24, 85%, 50%);
    }
    
    ${media.tablet} {
        height: ${props => props.slotHeight || 55}px;
    }
    
    ${media.mobile} {
        height: auto;
        min-height: 50px;
        border: none;
        border-radius: 6px;
        margin-bottom: 8px;
        padding: 8px;
    }
`;

export const DayCellWithCursor = styled(DayCell)`
    cursor: pointer;
`;

export const WeekEvent = styled.div`
    position: absolute;
    left: 2px;
    right: 2px;
    padding: 8px 12px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
    color: #FFFFFF;
    background: ${props => props.variant || '#23AAAF'};
    box-shadow: 0px 6px 12px rgba(15, 23, 42, 0.18);
    opacity: ${props => props.isPast ? 0.7 : 1};
    cursor: pointer;
    z-index: 1;
    box-sizing: border-box;
    overflow: hidden;
    
    &.dropdown-open {
        z-index: 9999999 !important;
        cursor: default;
        overflow: visible;
    }
    
    ${media.tablet} {
        left: 1px;
        right: 1px;
        padding: 6px 8px;
        font-size: 11px;
        border-radius: 8px;
    }
    
    ${media.mobile} {
        position: static;
        margin-bottom: 8px;
        padding: 8px 10px;
        font-size: 11px;
        border-radius: 6px;
        left: auto;
        right: auto;
    }
`;

export const WeekEventDynamic = styled(WeekEvent)`
    top: ${props => props.top}px;
    height: ${props => props.height}px;
    display: flex;
`;

export const WeekEventDynamicWithAlignment = styled(WeekEventDynamic)`
    justify-content: space-between;
    align-items: ${props => props.isLongAppointment ? 'flex-start' : 'center'};
`;

export const WeekEventDynamicWithZIndex = styled(WeekEventDynamicWithAlignment)`
    z-index: ${props => props.zIndex};
`;

export const WeekEventContent = styled.div`
    display: flex;
    flex-direction: ${props => props.isLongAppointment ? 'column' : 'row'};
    align-items: ${props => props.isLongAppointment ? 'flex-start' : 'center'};
    flex: 1;
    min-width: 0;
`;

export const WeekEventContentDynamic = styled.div`
    display: flex;
    flex-direction: ${props => props.isLongAppointment ? 'column' : 'row'};
    align-items: ${props => props.isLongAppointment ? 'flex-start' : 'center'};
    flex: 1;
    min-width: 0;
    font-size: ${props => props.fontSize}px;
    font-weight: 600;
    gap: ${props => props.isLongAppointment ? '0px' : '0px'};
    padding-top: ${props => props.isLongAppointment ? (props.isTablet ? '1px' : '2px') : '0px'};
    padding-bottom: ${props => props.isLongAppointment ? (props.isTablet ? '1px' : '0px') : '0px'};
`;

export const WeekEventNameContainer = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventTimeContainer = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventShortName = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const WeekEventNameText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventTimeText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventShortNameText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const WeekEventActionsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const WeekEventNameTextWithSize = styled(WeekEventNameText)`
    width: 100%;
    font-size: ${props => props.fontSize}px;
    line-height: ${props => props.lineHeight};
`;

export const WeekEventTimeTextWithSize = styled(WeekEventTimeText)`
    width: 100%;
    font-size: ${props => props.fontSize}px;
    opacity: 0.9;
    line-height: ${props => props.lineHeight};
`;

export const WeekEventTimeDisplay = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    margin-right: 8px;
    flex-shrink: 0;
    font-size: ${props => props.fontSize}px;
    opacity: 0.8;
    font-weight: 600;
    min-width: 35px;
    align-self: center;
`;

export const WeekEventStatusContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    flex-shrink: 0;
    margin-top: ${props => props.isLongAppointment ? (props.isTablet ? '0px' : '1px') : '0px'};
    align-self: ${props => props.isLongAppointment && props.isTablet ? 'center' : 'auto'};
`;

export const WeekEventEmailProblemIcon = styled.div`
    position: absolute;
    right: 34px;
    top: 4px;
    min-width: 25px;
    height: 19px;
    padding: 0 5px;
    border-radius: 999px;
    background: #FEE2E2;
    color: #B91C1C;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 900;
    line-height: 1;
    z-index: 4;
    pointer-events: none;

    ${media.tablet} {
        right: 28px;
        top: 3px;
        min-width: 22px;
        height: 17px;
        font-size: 9px;
    }
`;

export const CurrentTimeIndicator = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #ef4444, #f97316);
    z-index: 4;
    box-shadow: 0 0 4px rgba(239, 68, 68, 0.3);
    
    &::before {
        content: '';
        position: absolute;
        left: -6px;
        top: -4px;
        width: 10px;
        height: 10px;
        background: #ef4444;
        border-radius: 50%;
        box-shadow: 0 0 4px rgba(239, 68, 68, 0.4);
    }
`;

export const CurrentTimeIndicatorDynamic = styled(CurrentTimeIndicator)`
    top: ${props => props.top}px;
`;

export const CurrentTimeLabel = styled.div`
    position: absolute;
    left: 8px;
    top: -12px;
    background: #ef4444;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

// Indicador compacto de email para la vista Semana.
// Si no aparece ningún sobre, el turno no tiene notificación por email programada.
export const WeekEventNotificationBadge = styled.div`
    position: absolute;
    right: 36px;
    top: 4px;
    min-width: 34px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 9px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -0.2px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
    z-index: 3;
    cursor: help;

    background: ${props => {
        if (props.$variant === 'process') {
            return '#FEF3C7';
        }

        if (props.$variant === 'ok') {
            return '#DCFCE7';
        }

        if (props.$variant === 'problem' || props.$variant === 'spam') {
            return '#FEE2E2';
        }

        return '#E5E7EB';
    }};

    color: ${props => {
        if (props.$variant === 'process') {
            return '#92400E';
        }

        if (props.$variant === 'ok') {
            return '#166534';
        }

        if (props.$variant === 'problem' || props.$variant === 'spam') {
            return '#991B1B';
        }

        return '#374151';
    }};

    ${media.tablet} {
        right: 30px;
        top: 3px;
        min-width: 30px;
        height: 16px;
        padding: 0 4px;
        font-size: 8px;
    }
`;
